import React, { useEffect, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import Modal, { modalStyles as m } from "./Modal";
import { getCoupons } from "../data/queries";
import { applyCoupon, clearOrderDiscount } from "../../../Redux/features/WebRegister/cartSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";
import { filterOrderLevelCoupons, deziUpcSetOf, hasDeziItems } from "../engine";

// Defensive field mapping — coupon record shape from the Go bundle may vary.
const pick = (o, ...keys) => {
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k];
  return undefined;
};
const toCoupon = (c) => ({
  flag: pick(c, "flag", "coupon_type") ?? "1",
  discount: pick(c, "discount", "coupon_discount", "amount") ?? 0,
  minAmount: pick(c, "min_amount", "minAmount", "minimum_amount") ?? 0,
  maximumDiscount: pick(c, "maximum_discount", "maximumDiscount", "max_discount") ?? 0,
  name: pick(c, "name", "coupon_code", "code", "title") ?? "Coupon",
  id: pick(c, "id") ?? "",
});

export default function CouponModal({ onClose }) {
  const dispatch = useDispatch();
  const store = useStore();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    getCoupons().then(setList);
  }, []);

  // QV-4738 (Android setupAddCouponsDialog): the cart-level dialog shows ORDER-level coupons only
  // (type "0"/legacy null) valid for today. Item-level coupons (type 1/2) apply per line in the
  // item editor. is_disabled is ONLINE-only and intentionally NOT checked on the register.
  const rows = filterOrderLevelCoupons(list).filter((c) => {
    const label = String(pick(c, "name", "coupon_code", "code", "title") || "").toLowerCase();
    return label.includes(filter.toLowerCase());
  });

  const choose = (c) => {
    const coupon = toCoupon(c);
    const cart = store.getState().webRegisterCart;
    // Android setupAddCouponsDialog: NO coupon (of either type) may join an existing % discount.
    if (Number(cart.orderPercentDiscount) > 0) {
      return ToastifyAlert("Percentage discount is already applied.", "error");
    }
    const before = (cart.orderDiscounts || []).length;
    dispatch(applyCoupon(coupon));
    // applyCoupon no-ops when the min-amount gate fails — reflect the true outcome.
    if ((store.getState().webRegisterCart.orderDiscounts || []).length > before) {
      ToastifyAlert(`Applied ${coupon.name}`, "success");
      onClose();
    } else if (hasDeziItems(cart.lines || [], deziUpcSetOf(cart.deziDiscounts))) {
      // QV-7124 rule 4: manufacturer-discounted money does not count toward a coupon minimum, so the
      // generic wording would read as a bug on, say, a $250 cart failing a $100 minimum. Only shown
      // when a dezi line is actually present — otherwise it IS an ordinary minimum failure.
      ToastifyAlert(
        `This coupon needs a ${fmt(coupon.minAmount)} minimum on items without a manufacturer discount.`,
        "error"
      );
    } else {
      ToastifyAlert("Coupon minimum amount not met", "error");
    }
  };

  return (
    <Modal title="Apply coupon" onClose={onClose} width={460}>
      <input style={m.input} placeholder="Search coupons" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <div style={{ marginTop: 12, maxHeight: 340, overflow: "auto" }}>
        {rows.length === 0 && <div style={{ color: "var(--ink-3)", padding: 16, textAlign: "center" }}>No coupons</div>}
        {rows.map((c) => {
          const cp = toCoupon(c);
          return (
            <div
              key={cp.id || cp.name}
              onClick={() => choose(c)}
              style={{ display: "flex", justifyContent: "space-between", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 10, marginBottom: 8, cursor: "pointer" }}
            >
              <span style={{ fontWeight: 500 }}>{cp.name}</span>
              <span style={{ color: "var(--accent)", fontWeight: 500 }}>{cp.flag === "1" ? fmt(cp.discount) : `${cp.discount}%`}</span>
            </div>
          );
        })}
      </div>
      <button style={m.ghost} onClick={() => { dispatch(clearOrderDiscount()); onClose(); }}>Remove coupon</button>
    </Modal>
  );
}
